// Airtable mirror check — portal is master; the only fix is a re-push.
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, RefreshCw, Upload } from "lucide-react";
import { toast } from "sonner";
import { getLegalAuditAirtable, repushLegalAuditAirtable } from "@/lib/api";

const FIELD_LABEL = { stage: "Stage", owner: "Owner", next_action_date: "Next action date" };

export function AuditAirtable({ canPublish }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const [pushing, setPushing] = useState(false);

  const check = async () => {
    setLoading(true); setPicked(new Set());
    try { setData(await getLegalAuditAirtable()); }
    catch (e) { toast.error("Airtable check failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };
  const rows = data?.rows || [];
  const allPicked = rows.length > 0 && rows.every(r => picked.has(r.case_id));
  const toggle = (id) => setPicked(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const repush = async () => {
    const ids = Array.from(picked);
    if (!ids.length || !window.confirm(`Re-push stage, owner and next-action date for ${ids.length} file${ids.length === 1 ? "" : "s"} to Airtable? Nothing is pulled back.`)) return;
    setPushing(true);
    try {
      const r = await repushLegalAuditAirtable(ids);
      toast.success(`Airtable: ${r.pushed.length} re-pushed${r.failed.length ? `, ${r.failed.length} failed` : ""}`);
      await check();
    } catch (e) { toast.error("Re-push failed: " + (e?.response?.data?.detail || e.message)); }
    finally { setPushing(false); }
  };

  return (
    <Card data-testid="audit-airtable">
      <CardHeader className="py-2.5 px-4 border-b flex flex-row items-center gap-3 flex-wrap">
        <CardTitle className="text-sm font-medium">Airtable mirror</CardTitle>
        <span className="text-[11px] text-muted-foreground">Portal is master — mismatches are fixed by re-pushing to Airtable, never by pulling back.</span>
        <div className="ml-auto flex items-center gap-2">
          {picked.size > 0 && (
            <Button size="sm" className="h-8 text-xs bg-red-700 hover:bg-red-800 text-white" onClick={repush} disabled={pushing || !canPublish} title={canPublish ? "" : "Publisher permission needed"} data-testid="audit-airtable-repush">
              {pushing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />} Re-push {picked.size}
            </Button>
          )}
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={check} disabled={loading} data-testid="audit-airtable-check">
            {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />} {data ? "Re-check" : "Check mirror"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {!data && !loading && <div className="px-4 py-6 text-xs text-muted-foreground" data-testid="audit-airtable-idle">Not checked yet — compares stage, owner and next-action date on every file that has an Airtable record.</div>}
        {data && !data.ok && <div className="px-4 py-3 text-xs text-rose-700" data-testid="audit-airtable-error">{data.error}</div>}
        {data?.ok && (
          <>
            <div className="px-4 py-2 text-[11px] text-muted-foreground border-b" data-testid="audit-airtable-summary">
              {data.airtable_records} Airtable records · {data.matched} matched to a file · <span className={data.count ? "text-amber-700 font-semibold" : "text-emerald-700 font-semibold"}>{data.count} mismatch{data.count === 1 ? "" : "es"}</span>
            </div>
            {rows.length > 0 && (
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="w-8 px-3 py-2"><Checkbox checked={allPicked} onCheckedChange={() => setPicked(allPicked ? new Set() : new Set(rows.map(r => r.case_id)))} aria-label="Select all" data-testid="audit-airtable-select-all" /></th>
                    <th className="text-left px-2 py-2">Customer</th><th className="text-left px-2 py-2">Journey</th><th className="text-left px-2 py-2">Field</th><th className="text-left px-2 py-2">Portal</th><th className="text-left px-2 py-2">Airtable</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.case_id} className="border-t hover:bg-slate-50" data-testid={`audit-airtable-row-${r.case_id}`}>
                      <td className="px-3 py-2" onClick={() => toggle(r.case_id)}><Checkbox checked={picked.has(r.case_id)} className="pointer-events-none" aria-label={`Select ${r.customer_name}`} /></td>
                      <td className="px-2 py-2"><div className="font-medium">{r.customer_name}</div><div className="font-mono text-[10px] text-muted-foreground">{r.loan_application_id}</div></td>
                      <td className="px-2 py-2 text-slate-600">{r.journey}</td>
                      <td className="px-2 py-2">{r.mismatch.map(m => <div key={m}>{FIELD_LABEL[m] || m}</div>)}</td>
                      <td className="px-2 py-2 text-slate-800">{r.mismatch.map(m => <div key={m}>{r.portal[m] || <span className="text-slate-400">—</span>}</div>)}</td>
                      <td className="px-2 py-2 text-amber-800">{r.mismatch.map(m => <div key={m}>{r.airtable[m] || <span className="text-slate-400">—</span>}</div>)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
